import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  DisplayResult,
  PhaseStep,
  ResponsePhase,
  ToolCategory,
} from "../types";
import { tryParseDisplayResult } from "../utils/displayTool";
import { formatToolName } from "../utils/toolCall";

type StreamAccumulatorState = {
  phases: ResponsePhase[];
  textContent: string;
  displays: DisplayResult[];
  thinkingContent: string;
  thinkingDurationSeconds?: number;
  groundednessReplacement?: string;
};

const EMPTY_STATE: StreamAccumulatorState = {
  phases: [],
  textContent: "",
  displays: [],
  thinkingContent: "",
};

const ensurePhase = (
  current: StreamAccumulatorState,
  phaseId: "understand" | "fetch",
): StreamAccumulatorState => {
  const existing = current.phases.find((p) => p.id === phaseId);
  if (existing && existing.status === "active") return current;

  const closedPhases = current.phases.map((p) =>
    p.status === "active" ? { ...p, status: "complete" as const } : p,
  );

  if (existing) {
    return {
      ...current,
      phases: closedPhases.map((p) =>
        p.id === phaseId ? { ...p, status: "active" as const } : p,
      ),
    };
  }

  return {
    ...current,
    phases: [
      ...closedPhases,
      { id: phaseId, status: "active", steps: [] },
    ],
  };
};

const addStepToPhase = (
  current: StreamAccumulatorState,
  phaseId: "understand" | "fetch",
  step: PhaseStep,
): StreamAccumulatorState => {
  const withPhase = ensurePhase(current, phaseId);
  return {
    ...withPhase,
    phases: withPhase.phases.map((p) =>
      p.id === phaseId ? { ...p, steps: [...p.steps, step] } : p,
    ),
  };
};

const completeStep = (
  current: StreamAccumulatorState,
  callId: string,
  result: string | null,
): StreamAccumulatorState => ({
  ...current,
  phases: current.phases.map((p) => ({
    ...p,
    steps: p.steps.map((s) =>
      s.callId === callId ? { ...s, isComplete: true, result: result ?? undefined } : s,
    ),
  })),
});

const closeAllPhases = (current: StreamAccumulatorState): StreamAccumulatorState => ({
  ...current,
  phases: current.phases.map((p) =>
    p.status === "active" ? { ...p, status: "complete" } : p,
  ),
});

export function useStreamAccumulator() {
  const [state, setState] = useState<StreamAccumulatorState>(EMPTY_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;
  const { t } = useTranslation();

  const reset = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  const onThinking = useCallback((content: string) => {
    setState((prev) => {
      const withPhase = ensurePhase(prev, "understand");
      return { ...withPhase, thinkingContent: withPhase.thinkingContent + content };
    });
  }, []);

  const onThinkingEnd = useCallback((durationSeconds: number) => {
    setState((prev) => ({ ...prev, thinkingDurationSeconds: durationSeconds }));
  }, []);

  const onToolCallStart = useCallback(
    (toolName: string, callId: string, category: ToolCategory) => {
      setState((prev) => {
        // Display tools don't appear in phases — they render inline
        if (category === "display") return prev;

        const phaseId = category === "knowledge" ? "understand" : "fetch";
        const translationKey = `captain.tools.${toolName}`;
        const translated = t(translationKey, { defaultValue: "" });
        const displayName = translated && translated !== translationKey
          ? translated
          : formatToolName(toolName);

        const step: PhaseStep = {
          name: displayName,
          rawName: toolName,
          toolCategory: category,
          isComplete: false,
          callId,
        };

        return addStepToPhase(prev, phaseId, step);
      });
    },
    [t],
  );

  const onToolCallEnd = useCallback(
    (_toolName: string, callId: string, category: ToolCategory, result: string | null) => {
      setState((prev) => {
        // Display tools never appear in phases — render as rich UI or drop silently
        if (category === "display") {
          if (result) {
            const payload = tryParseDisplayResult(result);
            if (payload) {
              return {
                ...prev,
                displays: [...prev.displays, { payload, callId }],
              };
            }
          }
          return prev;
        }

        return completeStep(prev, callId, result);
      });
    },
    [],
  );

  const onContent = useCallback((content: string) => {
    setState((prev) => {
      const closed = closeAllPhases(prev);
      return { ...closed, textContent: closed.textContent + content };
    });
  }, []);

  const onContentReplace = useCallback((content: string) => {
    setState((prev) => ({
      ...prev,
      textContent: content,
      groundednessReplacement: content,
    }));
  }, []);

  return {
    state,
    stateRef,
    reset,
    callbacks: {
      onThinking,
      onThinkingEnd,
      onToolCallStart,
      onToolCallEnd,
      onContent,
      onContentReplace,
    },
  };
}
